import { fabric } from "fabric";
import { useState, useContext, useEffect, useCallback } from "react";
import { NotificationsContext } from "@ui-components/Notifications";
import CountryContext from "../../../../CountryContext";
import { useIntl } from "react-intl";

import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import useWindowResize from "./useWindowResize";
import useCanvasState from "./useCanvasState";

const fetchPreSignedUrl = async (imgUrl, countryApiRequest, intl, push) => {
  try {
    const psu = await countryApiRequest.post(`/aws-signed-url`, {
      content_type: "",
      format: "",
      prefix: imgUrl.split(".com/").pop(),
      bucket: "worksite_images",
      method: "GET",
    });
    return psu;
  } catch (e) {
    push({
      type: "error",
      title: intl.formatMessage({ id: "server_error" }),
    });
    throw e;
  }
};

const setBackgroundImage = async (canvas, preSignedUrl, isMounted) => {
  let g_resolve;
  const promise = new Promise((resolve, reject) => {
    g_resolve = resolve;
  });
  try {
    fabric.Image.fromURL(
      preSignedUrl,
      (img) => {
        if (canvas && isMounted) {
          canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
            // crossOrigin: 'anonymous',
            originX: "left",
            originY: "top",
          });
        }

        g_resolve();
      },
      { crossOrigin: "Anonymous" }
    );
  } catch (e) {
    g_resolve();
  }
  return promise;
};

const restoreCanvas = async (canvas, objectState) => {
  let g_resolve;
  const promise = new Promise((resolve, reject) => {
    g_resolve = resolve;
  });
  try {
    canvas.loadFromJSON(objectState, () => {
      canvas.renderAll();
      g_resolve();
    });
  } catch (e) {
    g_resolve();
  }
  return promise;
};

const useCanvasInit = (
  canvasRef,
  containerRef,
  imgUrl,
  taskId,
  taskMapId,
  initialState = null,
  zoomOnPin = false
) => {
  // contexts
  const { country } = useContext(CountryContext);

  // hooks
  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance(country.code);
  const [canvas, setCanvas] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [preSignedUrl, setPresignedUrl] = useState(null);
  const { push } = useContext(NotificationsContext);
  const { fetchCanvasState } = useCanvasState(
    canvas,
    taskId,
    taskMapId,
    initialState
  );

  const resizecanvas = useCallback(() => {
    try {
      if (canvas) {
        const newWidth = containerRef.current?.offsetWidth ?? 0;
        const newHeight = containerRef.current?.offsetHeight ?? 0;
        const size = Math.min(newWidth, newHeight);
        canvas.setWidth(size);
        canvas.setHeight(size);
        canvas.calcOffset();
        canvas.renderAll();
      }
    } catch (e) {}
  }, [containerRef]); // eslint-disable-line react-hooks/exhaustive-deps

  useWindowResize(resizecanvas);

  const zoomAndPanToFitImage = (canvas, backgroundImage) => {
    if (!canvas || !backgroundImage) return;
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();
    const origWidth = backgroundImage.getScaledWidth();
    const origHeight = backgroundImage.getScaledHeight();
    const angle = backgroundImage.angle;
    // account for image rotation
    const [imgWidth, imgHeight] =
      angle === 90 || angle === 270
        ? [origHeight, origWidth]
        : [origWidth, origHeight];

    // Calculate the zoom level to fit the image within the canvas
    const zoomWidth = canvasWidth / imgWidth;
    const zoomHeight = canvasHeight / imgHeight;
    const zoomLevel = Math.min(zoomWidth, zoomHeight);

    // Set the zoom level
    canvas.setZoom(zoomLevel);

    // Calculate the new image dimensions after zooming
    const newImgWidth = origWidth * zoomLevel;
    const newImgHeight = origHeight * zoomLevel;

    // pan the canvas centering the image
    const x = -(canvasWidth - newImgWidth) / 2;
    const y = -(canvasHeight - newImgHeight) / 2;
    canvas.absolutePan({ x, y });

    backgroundImage.setCoords();
  };

  const zoomToObject = (canvas, object, scale, showbottom = false) => {
    const zoom = (canvas.getWidth() * scale) / (object.width * object.scaleX); // object must occupy scale% of the canvas width
    canvas.setZoom(1); // reset zoom to 1 for accurate pan

    const vpw = canvas.width / zoom;
    const vph = canvas.height / zoom;
    // center the object horizontally
    const x = object.left + (object.width * object.scaleX) / 2 - vpw / 2;
    // used for pins, because they point to a bottom area
    const deltaY = showbottom ? (object.height * object.scaleY) / 2 : 0;
    // center the object vertically (with a delta if showbottom is true)
    const y =
      object.top + (object.height * object.scaleY) / 2 + deltaY - vph / 2;
    canvas.absolutePan({ x, y });
    canvas.setZoom(zoom);
  };

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    try {
      const canvasEl = document.createElement("canvas");
      // set canvasEl as only child in canvasref
      canvasRef.current.innerHTML = "";
      canvasRef.current.appendChild(canvasEl);
      const canvas = new fabric.Canvas(canvasEl, {
        height: containerRef.current?.offsetHeight ?? 0,
        width: containerRef.current?.offsetWidth ?? 0,
        backgroundColor: "#fff",
      });
      (async () => {
        const purl = await fetchPreSignedUrl(
          imgUrl,
          countryApiRequest,
          intl,
          push
        );
        const objectState = await fetchCanvasState();
        if (objectState) {
          await restoreCanvas(canvas, objectState);
        }
        await setBackgroundImage(canvas, purl, isMounted, objectState);

        zoomAndPanToFitImage(canvas, canvas.backgroundImage);
        if (zoomOnPin) {
          // find pin
          const pin = objectState?.objects?.find((o) => o.name === "pin");
          if (!!pin) {
            zoomToObject(canvas, pin, 0.16, true);
          }
        }
        canvas._methods = {
          zoomAndPanToFitImage,
          zoomToObject,
        };
        canvas.renderAll();

        setPresignedUrl(purl);
        setCanvas(canvas);
        // resizecanvas();
        setIsLoading(false);
      })();

      return () => {
        isMounted = false;
        // canvas.dispose();
        canvasEl.remove();
      };
    } catch (e) {
      console.log(e);
      setIsLoading(false);
    }
  }, [
    countryApiRequest,
    imgUrl,
    intl,
    push,
    canvasRef,
    containerRef,
    // resizecanvas,
    fetchCanvasState,
    zoomOnPin,
  ]);

  return [canvas, preSignedUrl, isLoading];
};

export default useCanvasInit;
